import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  User,
  MapPin,
  Phone,
  Clock,
  Calendar,
  Package,
  Timer,
  ArrowLeftRight,
  AlertCircle,
  Locate,
  MessageCircle,
  Shield,
  Users,
  Star,
  Loader2,
  X,
  CheckCircle,
  Info,
  ExternalLink,
} from "lucide-react";
import { IncomingCallChecker } from "@/components/ChatCall";
import { toast } from "sonner";
import { Capacitor } from '@capacitor/core';

// Get backend URL - handles web, PWA, and native Capacitor
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  if (Capacitor.isNativePlatform()) {
    return 'https://bizilgo.com';
  }
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    if (protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:') {
      return 'https://bizilgo.com';
    }
    if (protocol === 'https:' || protocol === 'http:') {
      return window.location.origin;
    }
  }
  return 'https://bizilgo.com';
};

const API_URL = getBackendUrl();

const STATUS_MESSAGES = {
  pending: "Looking for a driver...",
  accepted: "Driver is on the way to pick you up",
  arrived: "Driver has arrived at pickup location",
  in_progress: "Ride in progress",
  completed: "Ride completed",
  cancelled: "Ride cancelled",
};

export const ActiveRideCard = ({
  activeRide,
  cardRef,
  driverLocation,
  onShowChat,
  onShowTrackingMap,
  onCancelRide,
  onCompleteRide,
  onIncomingCall,
  showNearestDrivers,
  setShowNearestDrivers,
  nearestDrivers,
  setNearestDrivers,
}) => {
  const [pendingSeconds, setPendingSeconds] = useState(0);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [completingRide, setCompletingRide] = useState(false);
  const [nearbyDriverCount, setNearbyDriverCount] = useState(null);
  
  // Ad banner state - supports multiple banners
  const [adBanners, setAdBanners] = useState([]);
  
  // Increase fare state
  const [fareIncrease, setFareIncrease] = useState(0);
  const [customFareIncrease, setCustomFareIncrease] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [updatingFare, setUpdatingFare] = useState(false);
  
  // Fetch nearby driver count for pending rides
  useEffect(() => {
    if (!activeRide || activeRide.status !== "pending") {
      setNearbyDriverCount(null);
      return;
    }
    
    const fetchDriverCount = async () => {
      try {
        const cityId = activeRide.city_id || '';
        const url = `${API_URL}/api/rides/nearest-drivers?lat=${activeRide.pickup.lat}&lng=${activeRide.pickup.lng}&vehicle_type=${activeRide.vehicle_type}&limit=50${cityId ? `&city_id=${cityId}` : ''}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          // Show drivers within range, not total online (more accurate for user)
          setNearbyDriverCount(data.total_within_range || data.drivers?.length || 0);
        }
      } catch (error) {
        console.error("Error fetching driver count:", error);
      }
    };
    
    fetchDriverCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDriverCount, 30000);
    return () => clearInterval(interval);
  }, [activeRide?.id, activeRide?.status]);
  
  // Track ad banner analytics
  const trackBannerEvent = async (bannerId, eventType) => {
    try {
      await fetch(`${API_URL}/api/settings/ad-banners/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          banner_id: bannerId,
          event_type: eventType,
          customer_id: activeRide?.customer_id || null,
          city: activeRide?.city_name || null,
          device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
        })
      });
    } catch (error) {
      // Silent fail - don't interrupt user experience for analytics
    }
  };
  
  // Fetch ad banner settings for pending rides
  useEffect(() => {
    if (!activeRide || activeRide.status !== "pending") {
      return;
    }
    
    const fetchAdBanners = async () => {
      try {
        // Extract city from pickup address for city-based banner filtering
        let cityParam = '';
        if (activeRide.pickup_address) {
          // Try to extract city from the address (usually after last comma or before state/pincode)
          const addressParts = activeRide.pickup_address.split(',').map(p => p.trim());
          if (addressParts.length >= 2) {
            // Take second-to-last part as it's usually the city
            cityParam = addressParts[addressParts.length - 2] || addressParts[0];
          } else {
            cityParam = addressParts[0];
          }
        }
        
        const url = cityParam 
          ? `${API_URL}/api/settings/ad-banners?city=${encodeURIComponent(cityParam)}`
          : `${API_URL}/api/settings/ad-banners`;
          
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          if (data.banners && data.banners.length > 0) {
            const enabledBanners = data.banners.filter(b => b.enabled);
            setAdBanners(enabledBanners);
            
            // Track impressions for all enabled banners
            enabledBanners.forEach(banner => {
              trackBannerEvent(banner.id, 'impression', cityParam);
            });
          }
        }
      } catch (error) {
        console.error("Error fetching ad banners:", error);
      }
    };
    
    fetchAdBanners();
  }, [activeRide?.id, activeRide?.status]);
  
  // Track how long ride has been pending
  useEffect(() => {
    if (!activeRide || activeRide.status !== "pending") {
      setPendingSeconds(0);
      return;
    }
    
    // Calculate initial seconds since ride was created
    const createdAt = new Date(activeRide.created_at);
    const initialSeconds = Math.floor((Date.now() - createdAt.getTime()) / 1000);
    setPendingSeconds(initialSeconds);
    
    // Update every second
    const interval = setInterval(() => {
      setPendingSeconds(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeRide?.id, activeRide?.status]);
  
  const canGetDriverList = pendingSeconds >= 120; // 2 minutes
  const timeUntilList = Math.max(0, 120 - pendingSeconds);
  
  const fetchNearestDrivers = async () => {
    if (!activeRide?.pickup?.lat || !activeRide?.pickup?.lng) {
      toast.error("Unable to get your location");
      return;
    }
    
    setLoadingDrivers(true);
    try {
      // Include city_id for proper max_pickup_distance filtering
      const cityId = activeRide.city_id || '';
      const url = `${API_URL}/api/rides/nearest-drivers?lat=${activeRide.pickup.lat}&lng=${activeRide.pickup.lng}&vehicle_type=${activeRide.vehicle_type}&limit=10${cityId ? `&city_id=${cityId}` : ''}`;
      
      console.log("[NearestDrivers] Fetching from:", url);
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("[NearestDrivers] Server error:", response.status, errorText);
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("[NearestDrivers] Response:", data);
      
      if (data.drivers && data.drivers.length > 0) {
        setNearestDrivers(data.drivers);
        setShowNearestDrivers(true);
        
        // Silently cancel the ride in the background
        try {
          await fetch(`${API_URL}/api/rides/${activeRide.id}/cancel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              cancelled_by: 'customer',
              reason: 'Customer requested nearby drivers list'
            })
          });
          toast.info("Ride cancelled. Contact a driver below.");
        } catch (cancelError) {
          console.error("Error cancelling ride:", cancelError);
        }
      } else {
        // Show more specific message based on response
        const message = data.message || "No online drivers available nearby";
        toast.error(message);
        console.log("[NearestDrivers] No drivers found:", message, "max_pickup_distance:", data.max_pickup_distance_km);
      }
    } catch (error) {
      console.error("Error fetching nearest drivers:", error);
      toast.error("Failed to fetch nearby drivers. Please try again.");
    } finally {
      setLoadingDrivers(false);
    }
  };

  // Handle fare increase
  const handleFareIncrease = async (amount) => {
    const increaseAmount = amount === 'custom' ? parseInt(customFareIncrease) || 0 : amount;
    if (increaseAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setUpdatingFare(true);
    try {
      const response = await fetch(`${API_URL}/api/rides/${activeRide.id}/increase-fare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ increase_amount: increaseAmount })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update fare");
      }
      
      const data = await response.json();
      setFareIncrease(prev => prev + increaseAmount);
      setShowCustomInput(false);
      setCustomFareIncrease('');
      toast.success(`Fare increased by ₹${increaseAmount}. Notifying riders...`);
    } catch (error) {
      console.error("Error increasing fare:", error);
      toast.error(error.message || "Failed to increase fare");
    } finally {
      setUpdatingFare(false);
    }
  };
  
  // Handle customer completing the ride
  const handleCompleteRide = async () => {
    if (!window.confirm("Are you sure you want to mark this ride as complete? This will end the ride and calculate the final fare.")) {
      return;
    }
    
    setCompletingRide(true);
    try {
      const response = await fetch(`${API_URL}/api/rides/${activeRide.id}/customer-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          completed_by: 'customer',
          reason: 'Customer marked ride as complete'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to complete ride');
      }
      
      toast.success("Ride completed successfully!");
      if (onCompleteRide) {
        onCompleteRide();
      }
    } catch (error) {
      console.error("Error completing ride:", error);
      toast.error(error.message || "Failed to complete ride");
    } finally {
      setCompletingRide(false);
    }
  };
  
  // Show nearest drivers list (persists even after ride cancellation)
  if (showNearestDrivers && nearestDrivers.length > 0) {
    return (
      <div className="p-4 bg-card border-b border-border slide-up" data-testid="nearest-drivers-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Nearest Drivers
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setShowNearestDrivers(false);
              setNearestDrivers([]);
            }}
            data-testid="close-drivers-list"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          Contact these drivers directly to book your ride
        </p>
        
        <div className="space-y-3">
          {nearestDrivers.map((driver, index) => (
            <div 
              key={driver.id}
              className="p-3 bg-muted/50 rounded-xl border border-border"
              data-testid={`driver-card-${index}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{driver.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {driver.vehicle_type} {driver.vehicle_subtype && `• ${driver.vehicle_subtype}`}
                    </p>
                    {driver.vehicle_number && (
                      <p className="text-xs text-muted-foreground font-mono">{driver.vehicle_number}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-primary">{driver.distance_km} km</p>
                  {driver.rating > 0 && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      {driver.rating.toFixed(1)}
                    </p>
                  )}
                </div>
              </div>
              
              <a
                href={`tel:${driver.phone}`}
                className="mt-3 w-full flex items-center justify-center gap-2 p-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-all"
                data-testid={`call-driver-${index}`}
              >
                <Phone className="w-4 h-4" />
                Call {driver.phone}
              </a>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-center text-muted-foreground mt-4">
          Tip: Mention your pickup location when calling
        </p>
      </div>
    );
  }
  
  // Don't render if no active ride and no drivers list to show
  if (!activeRide || activeRide.status === "completed") return null;

  return (
    <div
      ref={cardRef}
      className="bg-card border-b border-border slide-up"
      data-testid="active-ride-card"
    >
      {/* ========== PROMINENT OTP DISPLAY (When Driver Accepted or Arrived) ========== */}
      {activeRide.otp && (activeRide.status === "accepted" || activeRide.status === "arrived") && (
        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b-4 border-amber-400" data-testid="prominent-otp-display">
          <div className="flex items-center justify-center gap-3 mb-2">
            <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h3 className="text-xl font-bold text-amber-800">
              {activeRide.status === "arrived" ? "Share OTP with Driver" : "Your Ride OTP"}
            </h3>
          </div>
          <div className="flex justify-center">
            <div className="bg-white px-8 py-4 rounded-2xl border-3 border-amber-400 shadow-lg">
              <p className="text-4xl font-black tracking-[0.5em] text-amber-700 text-center">
                {activeRide.otp}
              </p>
            </div>
          </div>
          <p className="text-center text-sm text-amber-700 mt-3 font-medium">
            {activeRide.status === "arrived" 
              ? "Driver has arrived! Share this code to start your ride."
              : "Keep this code ready. Share it when driver arrives to start your ride."}
          </p>
        </div>
      )}

      {/* ========== SECTION 1: STATUS BAR ========== */}
      <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-sm font-semibold status-${activeRide.status}`}>
              {activeRide.status.replace("_", " ").toUpperCase()}
            </span>
            {activeRide.status === "pending" && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{STATUS_MESSAGES[activeRide.status]}</p>
        </div>
      </div>

      {/* ========== SECTION 2: RIDE DETAILS CARD ========== */}
      <div className="p-4 border-b border-border">
        {/* Pickup & Drop */}
        <div className="flex gap-3 mb-4">
          {/* Location dots line */}
          <div className="flex flex-col items-center pt-1">
            <div className="w-3 h-3 rounded-full bg-green-500 ring-2 ring-green-200" />
            <div className="w-0.5 h-10 bg-gradient-to-b from-green-500 to-red-500 my-1" />
            <div className="w-3 h-3 rounded-full bg-red-500 ring-2 ring-red-200" />
          </div>
          
          {/* Addresses */}
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-green-600 font-semibold mb-0.5">Pickup</p>
              <p className="text-sm text-foreground font-medium leading-tight">{activeRide.pickup?.address}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-0.5">Drop-off</p>
              <p className="text-sm text-foreground font-medium leading-tight">{(activeRide.dropoff || activeRide.drop)?.address}</p>
            </div>
          </div>
        </div>

        {/* Ride Stats Grid */}
        <div className="grid grid-cols-3 gap-2 p-3 bg-muted/50 rounded-xl">
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Distance</p>
            <p className="text-lg font-bold text-foreground">{activeRide.distance || '--'}</p>
            <p className="text-[10px] text-muted-foreground">km</p>
          </div>
          <div className="text-center border-x border-border">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Est. Time</p>
            <p className="text-lg font-bold text-foreground">{activeRide.duration || '--'}</p>
            <p className="text-[10px] text-muted-foreground">mins</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Fare</p>
            <p className="text-lg font-bold text-primary">₹{activeRide.fare || 0}</p>
            {activeRide.tip_amount > 0 && (
              <p className="text-[10px] text-green-600">+₹{activeRide.tip_amount} tip</p>
            )}
          </div>
        </div>

        {/* Vehicle Type Badge */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              {activeRide.vehicle_type?.toLowerCase().includes('car') ? (
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 5h8m-4-9v18" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground capitalize">{activeRide.vehicle_type || 'Bike'}</p>
              <p className="text-[10px] text-muted-foreground">{activeRide.booking_type === 'package' ? 'Package Delivery' : 'Passenger Ride'}</p>
            </div>
          </div>
          {activeRide.package_weight && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              {activeRide.package_weight} kg
            </span>
          )}
        </div>
      </div>

      {/* ========== SECTION 3: PROGRESS BAR (Pending Only) ========== */}
      {activeRide.status === "pending" && (
        <div className="p-4 border-b border-border bg-slate-50 dark:bg-slate-900/50">
          <div className="rounded-xl border border-border overflow-hidden bg-white dark:bg-slate-800 shadow-sm">
            {/* Header bar */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-2 flex items-center justify-between">
              <span className="text-white text-sm font-medium">Finding Riders</span>
              <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded">
                <Users className="w-3 h-3 text-white" />
                <span className="text-white text-xs font-medium">
                  {nearbyDriverCount !== null ? nearbyDriverCount : '...'} nearby riders
                </span>
              </div>
            </div>
            
            {/* Progress Content */}
            <div className="p-3">
              {!canGetDriverList ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-foreground">Searching nearby riders...</span>
                    <span className="text-xs font-mono font-bold text-primary">
                      {Math.round(((120 - timeUntilList) / 120) * 100)}%
                    </span>
                  </div>
                  
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden relative">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 via-green-500 to-green-400 transition-all duration-1000"
                      style={{ 
                        width: `${Math.round(((120 - timeUntilList) / 120) * 100)}%`,
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite linear'
                      }}
                    >
                      <div 
                        className="absolute inset-0 opacity-30"
                        style={{
                          backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.2) 75%, transparent 75%)',
                          backgroundSize: '16px 16px',
                          animation: 'moveStripes 0.5s linear infinite'
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Time until driver list</span>
                    <span className="font-mono font-bold text-foreground">
                      {Math.floor(timeUntilList / 60)}:{(timeUntilList % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-green-600 font-medium">Search complete!</span>
                    <span className="text-xs font-mono font-bold text-green-600">100%</span>
                  </div>
                  
                  <div className="h-5 bg-green-500 rounded flex items-center justify-center mb-3">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                  
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                    disabled={loadingDrivers}
                    onClick={fetchNearestDrivers}
                    data-testid="get-nearest-drivers-btn"
                  >
                    {loadingDrivers ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fetching...</>
                    ) : (
                      <><Users className="w-4 h-4 mr-2" />Get Nearby Drivers List</>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
          
          <style>{`
            @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
            @keyframes moveStripes { 0% { background-position: 0 0; } 100% { background-position: 16px 0; } }
          `}</style>
        </div>
      )}

      {/* ========== SECTION 4: INCREASE FARE & CANCEL (Pending Only) ========== */}
      {activeRide.status === "pending" && (
        <div className="p-4 border-b border-border space-y-3">
          {/* Increase Fare */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <p className="text-xs font-semibold text-green-700 dark:text-green-300 mb-1">Quick Booking</p>
              <div className="flex flex-wrap gap-1.5">
                {[10, 20, 30].map((amount) => (
                  <Button
                    key={amount}
                    variant="outline"
                    size="sm"
                    disabled={updatingFare}
                    onClick={() => handleFareIncrease(amount)}
                    className={`text-xs h-7 px-3 ${fareIncrease === amount ? 'bg-green-500 text-white border-green-500' : 'border-green-300 text-green-700 hover:bg-green-100'}`}
                    data-testid={`increase-fare-${amount}`}
                  >
                    +₹{amount}
                  </Button>
                ))}
              </div>
              {fareIncrease > 0 && (
                <p className="text-[10px] text-green-600 mt-1">New fare: ₹{(activeRide.fare || 0) + fareIncrease}</p>
              )}
            </div>
            
            {/* Cancel Button */}
            <Button
              variant="outline"
              size="sm"
              className="h-14 px-4 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
              onClick={onCancelRide}
              data-testid="cancel-ride-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ========== SECTION 5: AD BANNERS (Pending Only) ========== */}
      {activeRide.status === "pending" && adBanners.length > 0 && (
        <div className="p-4 border-b border-border space-y-3">
          {adBanners.map((banner, index) => (
            <a 
              key={banner.id || index}
              href={banner.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl overflow-hidden shadow-sm border border-border hover:shadow-md transition-shadow"
              data-testid={`pending-ride-ad-banner-${index}`}
              onClick={() => trackBannerEvent(banner.id, 'click')}
            >
              <div 
                className="relative h-28 flex items-center justify-center"
                style={{
                  backgroundImage: `url(${banner.background})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <span className="absolute top-2 left-2 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">Ad</span>
                <div className="text-center px-4" style={{ color: banner.text_color || '#ffffff', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                  <p className="text-base font-bold tracking-wide">{banner.headline}</p>
                  <p className="text-xs font-semibold">{banner.subheadline}</p>
                </div>
              </div>
              <div className="p-2.5 bg-card flex items-center justify-between">
                <div>
                  <p className="font-semibold text-xs text-foreground">{banner.title}</p>
                  <p className="text-[10px] text-muted-foreground">{banner.description}</p>
                </div>
                <div className="flex items-center gap-1 text-blue-600 text-[10px] font-medium">
                  <span>Learn More</span>
                  <ExternalLink className="w-2.5 h-2.5" />
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* ========== SECTION 6: DRIVER INFO (When Assigned) ========== */}
      {activeRide.driver_name && (
        <div className="p-4 border-b border-border">
          <DriverInfoCard ride={activeRide} onShowChat={onShowChat} />
        </div>
      )}

      {/* Incoming Call Checker */}
      {activeRide.driver_id && ["accepted", "arrived", "in_progress"].includes(activeRide.status) && (
        <IncomingCallChecker rideId={activeRide.id} userRole="customer" onIncomingCall={onIncomingCall} />
      )}

      {/* OTP moved to prominent position at top of card */}

      {/* ========== SECTION 7: BOOKING TYPE DETAILS ========== */}
      <div className="p-4 border-b border-border">
        <BookingTypeDetails ride={activeRide} />
      </div>

      {/* ========== SECTION 8: ACTION BUTTONS ========== */}
      <div className="p-4">
        {/* Track Driver Button */}
        {activeRide.driver_id && ["accepted", "arrived", "in_progress"].includes(activeRide.status) && (
          <Button
            className="w-full mb-3"
            onClick={onShowTrackingMap}
            data-testid="track-driver-btn"
          >
            <Locate className="w-4 h-4 mr-2" />
            Track Driver on Map
            {driverLocation && <span className="ml-2 w-2 h-2 bg-green-400 rounded-full animate-pulse" />}
          </Button>
        )}

        {/* Complete Ride Button - Show when ride is accepted, arrived, or in_progress */}
        {/* Allows customers to complete ride even if driver forgot to enter OTP */}
        {["accepted", "arrived", "in_progress"].includes(activeRide.status) && (
          <Button
            className="w-full mb-3 bg-green-600 hover:bg-green-700 text-white"
            onClick={handleCompleteRide}
            disabled={completingRide}
            data-testid="complete-ride-btn"
          >
            {completingRide ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Completing...</>
            ) : (
              <><CheckCircle className="w-4 h-4 mr-2" />Complete Ride</>
            )}
          </Button>
        )}

        {/* Cancel Button - Only for accepted status (pending has it in section 4) */}
        {activeRide.status === "accepted" && (
          <Button
            variant="outline"
            className="w-full border-destructive text-destructive hover:bg-destructive/10"
            onClick={onCancelRide}
            data-testid="cancel-ride-btn-accepted"
          >
            Cancel Ride
          </Button>
        )}
      </div>

      {/* ========== SECTION 10: DISCLAIMER (Pending Only) ========== */}
      {activeRide.status === "pending" && (
        <div className="p-4 bg-gradient-to-r from-purple-50/50 to-indigo-50/50 dark:from-purple-950/20 dark:to-indigo-950/20">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-purple-800 dark:text-purple-200 mb-1">
                Fare is Estimated Only
              </p>
              <p className="text-[10px] text-purple-700 dark:text-purple-300 leading-relaxed">
                Riders may adjust fare based on conditions. If unsatisfied with price, feel free to look for another rider.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BookingTypeDetails = ({ ride }) => {
  if (!ride.booking_type || ride.booking_type === "one_way") return null;

  const breakdown = ride.fare_breakdown || {};
  const details = ride.booking_details || {};

  return (
    <div className="mb-4 p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800" data-testid="booking-type-details">
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-1 bg-indigo-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
          {ride.booking_type === "hourly" && <Clock className="w-3 h-3" />}
          {ride.booking_type === "daily" && <Calendar className="w-3 h-3" />}
          {ride.booking_type === "two_way" && <ArrowLeftRight className="w-3 h-3" />}
          {ride.booking_type === "package" && <Package className="w-3 h-3" />}
          {breakdown.label || ride.booking_type.replace("_", " ").toUpperCase()}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        {ride.booking_type === "hourly" && (
          <>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <Timer className="w-3 h-3" />
              <span>Duration: {breakdown.hours || details.hours || 1}h</span>
            </div>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <MapPin className="w-3 h-3" />
              <span>KM Limit: {breakdown.km_limit || (details.hours || 1) * 10} km</span>
            </div>
            <div className="col-span-2 text-indigo-600 dark:text-indigo-400">
              Extra: ₹{breakdown.extra_km_rate || 12}/km beyond limit
            </div>
          </>
        )}

        {ride.booking_type === "daily" && (
          <>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <Calendar className="w-3 h-3" />
              <span>Duration: {breakdown.days || details.days || 1} day(s)</span>
            </div>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <MapPin className="w-3 h-3" />
              <span>KM Limit: {breakdown.km_limit || (details.days || 1) * 150} km</span>
            </div>
            <div className="col-span-2 text-indigo-600 dark:text-indigo-400">
              Extra: ₹{breakdown.extra_km_rate || 10}/km beyond limit
            </div>
          </>
        )}

        {ride.booking_type === "two_way" && (
          <>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <Clock className="w-3 h-3" />
              <span>Wait: {breakdown.wait_minutes || details.estimated_wait_minutes || 30} mins</span>
            </div>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <Timer className="w-3 h-3" />
              <span>Wait Charge: ₹{breakdown.waiting_charge || 60}</span>
            </div>
            <div className="col-span-2 text-indigo-600 dark:text-indigo-400">
              Rate: ₹{breakdown.waiting_rate || 2}/min waiting
            </div>
          </>
        )}

        {ride.booking_type === "package" && (
          <>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <Package className="w-3 h-3" />
              <span>Weight: {breakdown.package_weight_kg || details.package_weight_kg || 1} kg</span>
            </div>
            <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
              <AlertCircle className="w-3 h-3" />
              <span>Max: {breakdown.max_weight_kg || 10} kg</span>
            </div>
            <div className="col-span-2 text-indigo-600 dark:text-indigo-400">
              Extra: ₹{breakdown.extra_weight_rate || 5}/kg above max
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DriverInfoCard = ({ ride, onShowChat }) => (
  <div className="p-3 bg-muted/50 rounded-xl mb-3">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
        <User className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1">
        <p className="font-semibold text-foreground">{ride.driver_name}</p>
        <p className="text-sm text-muted-foreground">
          {ride.vehicle_type?.toUpperCase()}
          {ride.vehicle_subtype_name && ` • ${ride.vehicle_subtype_name}`}
        </p>
        {ride.vehicle_number && (
          <p className="text-sm font-medium text-primary">{ride.vehicle_number}</p>
        )}
      </div>
    </div>

    <div className="flex gap-2 mt-3 pt-3 border-t border-border/50">
      <button
        onClick={onShowChat}
        className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
        data-testid="chat-driver-btn"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="text-sm font-medium">Chat</span>
      </button>
      {ride.driver_phone && (
        <a
          href={`tel:${ride.driver_phone}`}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-lg bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-colors"
          data-testid="call-driver-phone-btn"
        >
          <Phone className="w-4 h-4" />
          <span className="text-sm font-medium">Call</span>
          <span className="text-xs text-green-700">{ride.driver_phone}</span>
        </a>
      )}
    </div>
  </div>
);

const OTPDisplay = ({ otp }) => (
  <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-xl" data-testid="ride-otp-display">
    <div className="flex items-center gap-2 mb-2">
      <Shield className="w-5 h-5 text-amber-600" />
      <p className="text-sm font-medium text-amber-800">Share this OTP with your driver</p>
    </div>
    <div className="flex justify-center">
      <span className="text-4xl font-bold tracking-widest text-amber-700 bg-white px-6 py-3 rounded-lg border border-amber-200">
        {otp}
      </span>
    </div>
    <p className="text-xs text-amber-600 text-center mt-2">
      Driver needs this to start your ride
    </p>
  </div>
);

export default ActiveRideCard;
